import { useEffect, useState } from 'react';
import api from '../api';
import { QUALITY_SERVICE_API_URL } from '../config';

/**
 * Rows for the Duplicate Tickets table.
 *
 * Rows are deliberately slim — no descriptions, for the bug or its candidates. The same ticket's
 * text reappears in every row it is a candidate for, so including it dominated the payload.
 * A dedicated per-bug endpoint for the matches dialog follows in QA-7428.
 *
 * `q` is only forwarded when `searchDescription` is set: key and summary are on the rows already,
 * so the table filters those itself, but a description search can only be answered server-side.
 * The toolbar debounces the term before it reaches here.
 */
export default function useDuplicateTickets({
  includeWithoutMatches = false,
  q = '',
  searchDescription = false,
  needsLinking = false,
  includeUntriaged = true
} = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const serverQuery = searchDescription ? q.trim() : '';

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    const params = {};
    if (needsLinking) {
      // The actionable backlog, flagged or not — see duplicates.service.ts's loadNeedsLinkingBugs.
      // Mutually exclusive with includeWithoutMatches/showAll, which the toolbar already enforces.
      params.needsLinking = '1';
    } else if (includeWithoutMatches) {
      params.includeWithoutMatches = '1';
    }
    // Defaults to included server-side too, so the param is only sent when narrowing the view —
    // see duplicates-query.dto.ts's toOptions. No effect when needsLinking is set (see above).
    if (!includeUntriaged) {
      params.includeUntriaged = '0';
    }
    if (serverQuery) {
      params.q = serverQuery;
      params.qd = '1';
    }

    api.get(`${QUALITY_SERVICE_API_URL}/hub/duplicates`, { params, signal: controller.signal })
      .then(res => {
        setData(res.data);
        setError(null);
        setLoading(false);
      })
      .catch(err => {
        // A superseded request is not a failure — leave the previous rows and loading state alone.
        if (err.code === 'ERR_CANCELED') return;
        setError(err);
        setLoading(false);
      });

    return () => controller.abort();
  }, [includeWithoutMatches, needsLinking, includeUntriaged, serverQuery]);

  return { data, loading, error };
}
